import { useState, useEffect, useRef } from 'react';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import {
  Building2, Car, Sparkles, ChevronRight, Check,
  Shield, Lock, Crown, Star, Zap, Home, Watch,
  Gem, Ship, Users, ArrowRight, Quote, Cpu, TrendingUp, BarChart3
} from 'lucide-react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { Logo } from '@/components/Logo';

const HERO_VIDEO = 'https://videos.pexels.com/video-files/29350490/12648937_1922_1080_60fps.mp4';
const HERO_FALLBACK = 'https://images.pexels.com/videos/29350490/4k-horizontal-4k-video-boat-culture-boat-life-29350490.jpeg?auto=compress&cs=tinysrgb&w=1920';
const INDIA_HERO_FALLBACK = 'https://images.pexels.com/photos/2104882/pexels-photo-2104882.jpeg?auto=compress&cs=tinysrgb&w=1920';

const COUNTRY_OPTIONS = [
  { code: 'uae', prefix: 'uae', label: 'UAE', flag: '🇦🇪' },
  { code: 'india', prefix: 'in', label: 'India', flag: '🇮🇳' },
];

/* ── Country-specific content ── */
const CONTENT = {
  uae: {
    heroEyebrow: 'Lifestyle Architecture',
    heroHeadline: <>Architect the life<br className="hidden sm:block" /> you were destined to live.</>,
    heroSub: 'ASTIIV BASGAT is the intelligent lifestyle ecosystem that orchestrates your home, transport, and elevated living — refined into one seamless private experience.',
    heroCta: 'Design Your Future',
    heroSignIn: 'Sign In',
    navCta: 'Begin Your Journey',
    heroVideo: HERO_VIDEO,
    heroFallback: HERO_FALLBACK,
    howLabel: 'The ASTIIV BASGAT Method',
    howIntro: 'A measured, three-step path from intent to elevated living.',
    steps: [
      { num: '1', title: 'Define your aspirations', desc: 'Share your investment scale, preferred address, residence direction, and lifestyle intent. Less a form, more a conversation — three quiet minutes.', Icon: Home },
      { num: '2', title: 'Receive your curated lifestyle', desc: 'Our intelligence engine composes a complete, in-alignment lifestyle portfolio — residence, automobile, timepiece, and bespoke services.', Icon: Sparkles },
      { num: '3', title: 'Refine until it feels inevitable', desc: 'Substitute, compare, and personalise every asset. A private concierge stands behind every decision until the vision feels effortless.', Icon: Gem },
    ],
    bundleLabel: 'A Glimpse of the Experience',
    bundleTitle: 'Your Personal Lifestyle Portfolio',
    bundleDesc: 'A curated composition of residence, transport, timepiece, and concierge — precisely aligned to a AED 20M lifestyle thesis.',
    bundleItems: [
      { type: 'Residence', icon: Building2, name: 'Dubai Marina Penthouse', detail: '3 BR  |  Sea View  |  Move-in ready', price: 'AED 15.2M', image: 'https://images.pexels.com/photos/31692135/pexels-photo-31692135.jpeg?auto=compress&cs=tinysrgb&w=400' },
      { type: 'Automobile', icon: Car, name: 'Rolls-Royce Spectre', detail: '2024  |  Bespoke commission', price: 'AED 1.8M', image: 'https://images.unsplash.com/photo-1671572123661-c462bd06253e?w=400&q=80' },
      { type: 'Timepiece', icon: Watch, name: 'Patek Philippe Nautilus', detail: 'Rose Gold  |  Reference 5711/1R', price: 'AED 650K', image: 'https://images.unsplash.com/photo-1691865179028-1729b766a5cd?w=400&q=80' },
      { type: 'Concierge', icon: Users, name: 'Resident Butler & Private Chef', detail: 'Full-time  |  In-residence', price: 'AED 240K / yr' },
    ],
    bundleTotal: 'AED 17.89M',
    bundleBudget: 'In alignment with AED 20M',
    pricingTitle: 'Companion Tiers',
    pricingDesc: 'Begin with the essential. Ascend as your vision sharpens.',
    plans: [
      { tier: 'free', name: 'Discover', price: 'AED 0', period: 'always', icon: Star, features: [
        { text: 'Personal lifestyle profile', included: true },{ text: 'Three curated lifestyle portfolios', included: true },{ text: 'Five residence previews each day', included: true },{ text: 'AI concierge — essentials', included: true },
        { text: 'Asset substitution within portfolios', included: false },{ text: 'Portfolio Intelligence dashboard', included: false },{ text: 'Off-market assets', included: false },{ text: 'Investor mode and ROI intelligence', included: false },
      ]},
      { tier: 'premium', name: 'Curate', price: 'AED 1,999', period: '/mo', icon: Crown, popular: true, features: [
        { text: 'Everything in Discover', included: true },{ text: 'Unlimited residence previews', included: true },{ text: 'One hundred lifestyle portfolios', included: true },{ text: 'Full AI concierge access', included: true },{ text: 'Investor mode with ROI intelligence', included: true },{ text: 'Portfolio Intelligence dashboard', included: true },
      ]},
      { tier: 'elite', name: 'Signature', price: 'AED 7,999', period: '/mo', icon: Zap, features: [
        { text: 'Everything in Curate', included: true },{ text: 'Unlimited lifestyle portfolios', included: true },{ text: 'Substitute any asset within portfolios', included: true },{ text: 'Portfolio Intelligence dashboard', included: true },{ text: 'Private concierge', included: true },{ text: 'Off-market assets', included: true },{ text: 'Signature experiences and opportunities', included: true },
      ]},
    ],
    aboutDesc: 'ASTIIV BASGAT is the intelligent ecosystem behind the lives ambitious people choose to build. Rather than navigating disjointed listings, you describe the life you intend to live — and our intelligence composes it. Residence, automobile, timepiece, concierge, and the quiet details in between — orchestrated into one refined, in-alignment experience.',
    aboutBadge: 'Crafted in Dubai',
    testimonials: [
      { name: 'Arjun Mehta', role: 'Investor, Mumbai', text: 'ASTIIV BASGAT revealed an entire Dubai lifestyle I would never have composed alone. The intelligence understood my intent before I could articulate it.' },
      { name: 'Sarah Al-Rashid', role: 'Entrepreneur, Dubai', text: 'Ten platforms became one quiet experience. Residence, automobile, concierge — precisely aligned. Effortlessly considered.' },
      { name: 'James Chen', role: 'Family Office, Singapore', text: 'The portfolio intelligence is exceptional. Substituting assets and seeing live scoring made every decision feel both rapid and resolved.' },
    ],
    finalCta: 'Your next chapter, intelligently composed.',
    finalDesc: 'Three quiet minutes. No commitment. Just a clearer vision of the life you intend to live.',
    finalCtaBtn: 'Begin Your Journey',
    footerLine: '\u00a9 2026 ASTIIV BASGAT. Crafted in Dubai. For the ambitious, everywhere.',
  },
  india: {
    heroHeadline: <>Plan your dream life,<br className="hidden sm:block" /> step by step</>,
    heroSub: 'AI builds your complete life plan \u2014 home, car, and lifestyle \u2014 within your budget',
    heroCta: 'Start Planning Free',
    heroSignIn: 'Sign In',
    navCta: 'Start Planning Free',
    heroVideo: null,
    heroFallback: INDIA_HERO_FALLBACK,
    howLabel: 'How It Works',
    steps: [
      { num: '1', title: 'Share your goals & budget', desc: 'Tell us your monthly income, savings target, dream city, and what matters most to you. Just 3 minutes.', Icon: Home },
      { num: '2', title: 'AI creates your life plan', desc: 'Our AI builds a realistic bundle \u2014 the right home, car, and lifestyle upgrades that fit your budget perfectly.', Icon: Sparkles },
      { num: '3', title: 'Explore & fine-tune', desc: 'Compare options, swap items, and adjust until your plan feels just right. No pressure, no pushy agents.', Icon: Gem },
    ],
    bundleLabel: 'See It In Action',
    bundleTitle: 'Your Smart Life Bundle',
    bundleDesc: 'Here\u2019s what a personalized life plan looks like \u2014 curated within a \u20b93 Cr budget',
    bundleItems: [
      { type: 'Property', icon: Building2, name: '3BHK in Powai, Mumbai', detail: '3 BR  |  Garden View  |  Gated Society', price: '\u20b92.1Cr', image: 'https://images.pexels.com/photos/2462015/pexels-photo-2462015.jpeg?auto=compress&cs=tinysrgb&w=400' },
      { type: 'Car', icon: Car, name: 'Toyota Fortuner', detail: '2025  |  Diesel  |  Top Variant', price: '\u20b945L', image: 'https://images.pexels.com/photos/116675/pexels-photo-116675.jpeg?auto=compress&cs=tinysrgb&w=400' },
      { type: 'Watch', icon: Watch, name: 'TAG Heuer Carrera', detail: 'Automatic  |  Stainless Steel', price: '\u20b93.5L', image: 'https://images.unsplash.com/photo-1524592094714-0f0654e20314?w=400&q=80' },
      { type: 'Lifestyle', icon: Sparkles, name: 'Club Membership + Gym', detail: 'Premium  |  Family  |  Annual', price: '\u20b92.5L/yr' },
    ],
    bundleTotal: '\u20b92.54Cr',
    bundleBudget: 'Within \u20b93Cr',
    pricingTitle: 'Completely Free to Use',
    pricingDesc: 'ASTIIV BASGAT India is free. We earn when you connect with verified partners.',
    plans: [
      { tier: 'free', name: 'Free', price: '\u20b90', period: 'always free', icon: Star, features: [
        { text: 'ASTIIV BASGAT profile creation', included: true },{ text: 'AI-powered lifestyle bundles (3)', included: true },{ text: '5 property views per day', included: true },{ text: 'Basic AI concierge', included: true },
        { text: 'Swap assets in bundles', included: false },{ text: 'Portfolio Insights Dashboard', included: false },{ text: 'Off-market assets', included: false },{ text: 'Investor mode & ROI tools', included: false },
      ]},
      { tier: 'premium', name: 'Premium', price: '\u20b924,999', period: '/mo', icon: Crown, popular: true, features: [
        { text: 'Everything in Free', included: true },{ text: 'Unlimited property views', included: true },{ text: '100 lifestyle bundles', included: true },{ text: 'Full AI concierge access', included: true },{ text: 'Investor mode with ROI calculator', included: true },{ text: 'Portfolio Insights Dashboard', included: true },
      ]},
      { tier: 'elite', name: 'Elite', price: '\u20b974,999', period: '/mo', icon: Zap, features: [
        { text: 'Everything in Premium', included: true },{ text: 'Unlimited lifestyle bundles', included: true },{ text: 'Swap any asset in bundles', included: true },{ text: 'Portfolio Insights Dashboard', included: true },{ text: 'Private concierge', included: true },{ text: 'Off-market assets', included: true },{ text: 'VIP experiences & exclusive deals', included: true },
      ]},
    ],
    aboutDesc: 'ASTIIV BASGAT India helps you plan your dream life \u2014 realistically. Tell us your income and goals, and our AI creates a complete life plan: the right home, car, and lifestyle upgrades. No fluff, no fake promises \u2014 just smart planning powered by AI.',
    aboutBadge: 'India-focused',
    testimonials: [
      { name: 'Priya Sharma', role: 'IT Professional, Bangalore', text: 'I always thought owning a 3BHK in a good society was out of reach. ASTIIV BASGAT showed me a plan that fits my salary. Now I\u2019m actually house-hunting!' },
      { name: 'Rahul Verma', role: 'Startup Founder, Delhi', text: 'The AI understood my budget perfectly. It suggested a realistic combo \u2014 a nice flat in Gurgaon, a Creta, and even a membership. All within my savings plan.' },
      { name: 'Ananya Iyer', role: 'Doctor, Mumbai', text: 'I love how practical it is. No luxury-only nonsense. It gave me options I can actually afford with my income, and the step-by-step plan is motivating.' },
    ],
    finalCta: 'Ready to plan your dream life?',
    finalDesc: 'It takes 3 minutes. Completely free \u2014 start planning now.',
    finalCtaBtn: 'Start Planning Free',
    footerLine: '\u00a9 2026 ASTIIV BASGAT India. All rights reserved.',
  },
};
const Reveal = ({ children, className = '', delay = 0 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 28 }} animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1], delay }} className={className}>
      {children}
    </motion.div>
  );
};

const serif = { fontFamily: "'Playfair Display', serif" };
const gold = '#C6A15B';


const LandingPage = () => {
  const navigate = useNavigate();
  const { country: urlCountry } = useParams();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [countryMenuOpen, setCountryMenuOpen] = useState(false);
  const [apiPlans, setApiPlans] = useState(null);

  // Derive active country from URL prefix
  const activePrefix = urlCountry?.toLowerCase() || '';
  const activeCountry = COUNTRY_OPTIONS.find(c => c.prefix === activePrefix) || COUNTRY_OPTIONS[0];
  const isIndia = activeCountry.code === 'india';
  const c = CONTENT[isIndia ? 'india' : 'uae'];

  // Fetch region-specific pricing from backend master plan system
  useEffect(() => {
    const region = isIndia ? 'IN' : 'UAE';
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/subscription/plans`, {
      headers: { 'X-Portal-Region': region },
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.plans) setApiPlans(data.plans); })
      .catch(() => {});
  }, [isIndia]);

  // Merge backend pricing into static plan content (features stay identical across regions via plans_master)
  const mergedPlans = c.plans.map(plan => {
    const live = apiPlans?.[plan.tier];
    if (!live) return plan;
    const currency = live.currency;
    const priceNum = Number(live.price) || 0;
    const priceDisplay = priceNum === 0
      ? (currency === 'INR' ? '\u20b90' : `${currency} 0`)
      : currency === 'INR'
        ? `\u20b9${priceNum.toLocaleString('en-IN')}`
        : `${currency} ${priceNum.toLocaleString()}`;
    return { ...plan, price: priceDisplay };
  });

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  // ── Patek Philippe-style soft parallax for hero entrance ──
  // Each element rises at a slightly different rate as the user scrolls,
  // with a gentle fade. Disabled on prefers-reduced-motion (handled by browser).
  const heroRef = useRef(null);
  const { scrollYProgress: heroProgress } = useScroll({
    target: heroRef,
    offset: ['start start', 'end start'],
  });
  const eyebrowY = useTransform(heroProgress, [0, 1], [0, -60]);
  const headlineY = useTransform(heroProgress, [0, 1], [0, -130]);
  const subY = useTransform(heroProgress, [0, 1], [0, -90]);
  const ctaY = useTransform(heroProgress, [0, 1], [0, -50]);
  const heroOpacity = useTransform(heroProgress, [0, 0.55, 0.95], [1, 0.85, 0]);
  const heroBgScale = useTransform(heroProgress, [0, 1], [1, 1.08]);
  const heroBgY = useTransform(heroProgress, [0, 1], [0, 80]);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white overflow-x-hidden" style={{ fontFamily: "'Inter', sans-serif" }}>

      {/* ━━━ NAVBAR ━━━ */}
      <nav className={`fixed w-full z-50 transition-all duration-500 ${scrolled ? 'bg-[#0A0A0A]/80 backdrop-blur-2xl border-b border-white/5 py-3' : 'bg-transparent py-5'}`} data-testid="landing-navbar">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12 flex justify-between items-center">
          <Link to="/" className="inline-flex items-center" data-testid="nav-logo">
            <Logo
              to={null}
              size="sm"
              wordmarkClassName="text-white"
              tracking="tracking-[0.2em]"
              testId="landing-nav-logo"
            />
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <button onClick={() => scrollTo('how-it-works')} className="text-[12px] text-white/50 hover:text-white transition-colors tracking-[0.15em] uppercase">How It Works</button>
            <button onClick={() => scrollTo('pricing')} className="text-[12px] text-white/50 hover:text-white transition-colors tracking-[0.15em] uppercase">Pricing</button>
            <button onClick={() => scrollTo('about')} className="text-[12px] text-white/50 hover:text-white transition-colors tracking-[0.15em] uppercase">About</button>
            {/* Country Switcher — uses ISO codes to avoid OS emoji-flag rendering issues */}
            <div className="relative">
              <button
                onClick={() => setCountryMenuOpen(!countryMenuOpen)}
                className="flex items-center gap-1.5 text-[11px] text-white/50 hover:text-white transition-colors tracking-[0.1em] uppercase px-3 py-1.5 border border-white/10 rounded-full hover:border-white/25 whitespace-nowrap"
                data-testid="country-switcher-btn"
              >
                <span className="font-semibold text-[#C6A15B]">{activeCountry.code === 'india' ? 'IN' : 'AE'}</span>
                <span className="hidden lg:inline">{activeCountry.label}</span>
                <ChevronRight className={`w-3 h-3 transition-transform ${countryMenuOpen ? 'rotate-90' : ''}`} />
              </button>
              {countryMenuOpen && (
                <div className="absolute top-full mt-2 right-0 bg-[#141414] border border-white/10 rounded-lg overflow-hidden shadow-2xl z-50 min-w-[140px]">
                  {COUNTRY_OPTIONS.map(opt => (
                    <button
                      key={opt.code}
                      onClick={() => { navigate(`/${opt.prefix}`); setCountryMenuOpen(false); }}
                      data-testid={`country-opt-${opt.code}`}
                      className={`flex items-center gap-2 w-full px-4 py-2.5 text-left text-sm hover:bg-white/5 transition-colors ${
                        opt.code === activeCountry.code ? 'text-[#C6A15B]' : 'text-white/70 hover:text-white'
                      }`}
                    >
                      <span className="font-semibold text-[10px] tracking-wider">{opt.code === 'india' ? 'IN' : 'AE'}</span>
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button onClick={() => navigate('/login')} className="text-[12px] text-white/50 hover:text-white transition-colors tracking-[0.15em] uppercase" data-testid="nav-signin-link">Sign In</button>
            <button onClick={() => navigate('/partner/login')} className="text-[12px] text-white/50 hover:text-white transition-colors tracking-[0.15em] uppercase hidden lg:inline-block" data-testid="nav-partner-login-link">Partner</button>
            <button onClick={() => navigate('/signup')}
              className="bg-[#C6A15B] text-black hover:bg-[#DCC286] px-7 py-2.5 text-[12px] tracking-[0.15em] uppercase transition-all duration-300 font-semibold hover:shadow-[0_0_30px_rgba(198,161,91,0.35)]"
              data-testid="nav-start-btn">
              {c.navCta}
            </button>
          </div>
          <button className="md:hidden p-2 text-white" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-toggle">
            {mobileMenuOpen ? <span className="text-2xl">&times;</span> : <span className="text-2xl">&#9776;</span>}
          </button>
        </div>
        {mobileMenuOpen && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="md:hidden absolute top-full left-0 right-0 bg-[#0A0A0A]/95 backdrop-blur-2xl border-b border-white/10 px-6 py-6 space-y-4">
            <button onClick={() => scrollTo('how-it-works')} className="block w-full text-left text-white/60 hover:text-white text-sm tracking-[0.1em] uppercase">How It Works</button>
            <button onClick={() => scrollTo('pricing')} className="block w-full text-left text-white/60 hover:text-white text-sm tracking-[0.1em] uppercase">Pricing</button>
            <button onClick={() => scrollTo('about')} className="block w-full text-left text-white/60 hover:text-white text-sm tracking-[0.1em] uppercase">About</button>
            {/* Mobile Country Switcher — ISO codes for universal rendering */}
            <div className="flex gap-2">
              {COUNTRY_OPTIONS.map(opt => (
                <button key={opt.code} onClick={() => { navigate(`/${opt.prefix}`); setMobileMenuOpen(false); }}
                  className={`flex-1 py-2 text-sm text-center border rounded-lg transition-colors ${opt.code === activeCountry.code ? 'border-[#C6A15B] text-[#C6A15B]' : 'border-white/10 text-white/50 hover:border-white/25'}`}>
                  <span className="font-semibold mr-1.5 text-[10px] tracking-wider">{opt.code === 'india' ? 'IN' : 'AE'}</span>{opt.label}
                </button>
              ))}
            </div>
            <button onClick={() => { navigate('/login'); setMobileMenuOpen(false); }} className="block w-full text-left text-white/60 hover:text-white text-sm tracking-[0.1em] uppercase">Sign In</button>
            <button onClick={() => { navigate('/partner/login'); setMobileMenuOpen(false); }} className="block w-full text-left text-white/60 hover:text-white text-sm tracking-[0.1em] uppercase" data-testid="mobile-partner-login-btn">Partner Login</button>
            <button onClick={() => { navigate('/partner'); setMobileMenuOpen(false); }} className="block w-full text-left text-white/60 hover:text-white text-sm tracking-[0.1em] uppercase" data-testid="mobile-partner-apply-btn">Become a Partner</button>
            <button onClick={() => { navigate('/signup'); setMobileMenuOpen(false); }} className="w-full bg-[#C6A15B] text-black py-3 text-sm tracking-[0.1em] uppercase font-semibold" data-testid="mobile-start-btn">{c.navCta}</button>
          </motion.div>
        )}
      </nav>


      {/* ━━━ 1. HERO ━━━ */}
      <section ref={heroRef} className="min-h-screen relative flex items-center justify-center text-center" data-testid="hero-section">
        <motion.div className="absolute inset-0 z-0 overflow-hidden" style={{ scale: heroBgScale, y: heroBgY }}>
          {c.heroVideo ? (
            <video autoPlay loop muted playsInline className="absolute inset-0 w-full h-full object-cover hidden md:block"
              style={{ animation: 'heroZoom 25s ease-in-out infinite alternate' }} poster={c.heroFallback} data-testid="hero-video">
              <source src={c.heroVideo} type="video/mp4" />
            </video>
          ) : (
            <img src={c.heroFallback} alt="" className="absolute inset-0 w-full h-full object-cover hidden md:block" style={{ animation: 'heroZoom 25s ease-in-out infinite alternate' }} />
          )}
          <img src={c.heroFallback} alt="" className="md:hidden absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/80 to-[#0A0A0A] z-10" />
          {/* Subtle vignette + film grain for premium cinematic feel */}
          <div className="absolute inset-0 z-10 pointer-events-none" style={{
            background: 'radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.55) 100%)'
          }} />
        </motion.div>

        <motion.div className="relative z-20 max-w-[820px] mx-auto px-6" style={{ opacity: heroOpacity }}>
          {c.heroEyebrow && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.2, delay: 0.15 }}
              style={{ y: eyebrowY }}
              className="flex items-center justify-center gap-3 mb-6"
              data-testid="hero-eyebrow"
            >
              <span className="h-px w-10 bg-[#C6A15B]/40" />
              <span className="text-[10px] tracking-[0.45em] uppercase text-[#C6A15B] font-medium">{c.heroEyebrow}</span>
              <span className="h-px w-10 bg-[#C6A15B]/40" />
            </motion.div>
          )}
          <motion.div initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1.2, delay: 0.3 }} style={{ y: headlineY }}>
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-tight mb-6 leading-[1.08] font-light" style={serif} data-testid="hero-headline">
              {c.heroHeadline}
            </h1>
          </motion.div>

          <motion.p initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.7 }}
            style={{ y: subY }}
            className="text-base md:text-lg text-white/55 max-w-[620px] mx-auto mb-10 font-light leading-relaxed" data-testid="hero-subheadline">
            {c.heroSub}
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 1.0 }} style={{ y: ctaY }} className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={() => navigate('/signup')}
              className="bg-[#C6A15B] text-black px-12 py-4 uppercase tracking-[0.2em] text-[13px] font-semibold transition-all duration-500 hover:bg-[#DCC286] hover:shadow-[0_0_40px_rgba(198,161,91,0.4)] flex items-center justify-center gap-2"
              data-testid="hero-cta-btn">
              {c.heroCta} <ArrowRight className="w-4 h-4" />
            </button>
            <button onClick={() => navigate('/login')}
              className="border border-white/20 text-white px-10 py-4 hover:bg-white/5 backdrop-blur-sm transition-all duration-500 uppercase tracking-[0.2em] text-[13px]"
              data-testid="hero-signin-btn">
              Sign In
            </button>
          </motion.div>
        </motion.div>

        <motion.div className="absolute bottom-10 left-1/2 -translate-x-1/2 hidden md:block"
          animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2.5, ease: 'easeInOut' }}>
          <div className="w-[22px] h-[36px] border border-white/20 rounded-full flex justify-center pt-2">
            <div className="w-[3px] h-[6px] bg-[#C6A15B] rounded-full" />
          </div>
        </motion.div>
      </section>


      {/* ━━━ 2. HOW IT WORKS ━━━ */}
      <section id="how-it-works" className="py-24 md:py-32 bg-[#0E0E0E]" data-testid="how-it-works-section">
        <div className="max-w-[1100px] mx-auto px-6 md:px-12">
          <Reveal>
            <div className="text-center mb-20">
              <span className="text-[#C6A15B] text-[11px] tracking-[0.3em] uppercase mb-4 block">{c.howLabel}</span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-light" style={serif}>How It Works</h2>
              {c.howIntro && (
                <p className="text-white/40 text-base mt-5 max-w-[560px] mx-auto leading-relaxed">{c.howIntro}</p>
              )}
            </div>
          </Reveal>

          <div className="grid md:grid-cols-3 gap-12 md:gap-6 relative">
            <div className="hidden md:block absolute top-[36px] left-[20%] w-[60%] h-[1px]">
              <div className="w-full h-full bg-gradient-to-r from-[#C6A15B]/40 via-[#C6A15B]/20 to-[#C6A15B]/40" />
            </div>

            {c.steps.map(({ num, title, desc, Icon }, i) => (
              <Reveal key={num} delay={i * 0.15}>
                <div className="flex flex-col items-center text-center" data-testid={`step-${i}`}>
                  <div className="w-[72px] h-[72px] rounded-full border-2 border-[#C6A15B]/30 flex items-center justify-center mb-7 relative bg-[#0E0E0E] z-10 group hover:border-[#C6A15B] transition-all duration-500">
                    <Icon className="w-6 h-6 text-[#C6A15B]" strokeWidth={1.5} />
                    <div className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-[#C6A15B] text-black text-xs font-bold flex items-center justify-center">{num}</div>
                  </div>
                  <h3 className="text-lg font-medium mb-3 tracking-wide" style={serif}>{title}</h3>
                  <p className="text-white/40 text-sm leading-relaxed max-w-[280px]">{desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>


      {/* ━━━ 3. EXAMPLE BUNDLE OUTPUT ━━━ */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]" data-testid="example-bundle-section">
        <div className="max-w-[1100px] mx-auto px-6 md:px-12">
          <Reveal>
            <div className="text-center mb-16">
              <span className="text-[#C6A15B] text-[11px] tracking-[0.3em] uppercase mb-4 block">{c.bundleLabel}</span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-light mb-4" style={serif}>{c.bundleTitle}</h2>
              <p className="text-white/40 text-base max-w-lg mx-auto">{c.bundleDesc}</p>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="bg-[#0E0E0E] border border-[#1F1F1F] rounded-2xl overflow-hidden max-w-3xl mx-auto hover:border-[#C6A15B]/20 transition-all duration-700" data-testid="sample-bundle-card">
              {/* Bundle header */}
              <div className="bg-gradient-to-r from-[#C6A15B]/15 to-transparent p-6 md:p-8 border-b border-white/5">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <Sparkles className="w-5 h-5 text-[#C6A15B]" />
                      <span className="text-[10px] text-[#C6A15B] tracking-[0.2em] uppercase font-semibold">AI Recommended</span>
                    </div>
                    <h3 className="text-xl md:text-2xl font-light" style={serif}>{isIndia ? 'Smart Life Plan' : 'Lifestyle Upgrade Plan'}</h3>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-white/40 mb-1">Match Score</div>
                    <div className="text-2xl font-semibold text-[#C6A15B]">{isIndia ? '94%' : '92%'}</div>
                  </div>
                </div>
              </div>

              {/* Bundle items */}
              <div className="divide-y divide-white/[0.04]">
                {c.bundleItems.map(({ type, icon: Icon, name, detail, price, image }) => (
                  <div key={name} className="flex items-center gap-3 sm:gap-4 p-4 sm:p-5 md:p-6 hover:bg-white/[0.02] transition-colors">
                    <div className="w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-16 rounded-xl overflow-hidden flex-shrink-0 border border-white/[0.06] bg-[#C6A15B]/10 relative">
                      {image && (
                        <img
                          src={image}
                          alt={name}
                          className="w-full h-full object-cover absolute inset-0"
                          loading="lazy"
                          referrerPolicy="no-referrer"
                          onError={(e) => {
                            // Image CDN blocked (corp/mobile network, ad-blocker) — degrade
                            // gracefully to the icon fallback that sits beneath it.
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      )}
                      <div className="absolute inset-0 flex items-center justify-center">
                        <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-[#C6A15B]" strokeWidth={1.5} />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] sm:text-xs text-[#C6A15B]/60 uppercase tracking-wider mb-0.5">{type}</div>
                      <div className="text-white font-medium text-sm md:text-base truncate">{name}</div>
                      <div className="text-white/30 text-[11px] sm:text-xs truncate">{detail}</div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-white font-medium text-xs sm:text-sm md:text-base whitespace-nowrap">{price}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Bundle footer */}
              <div className="p-6 md:p-8 bg-[#0A0A0A] border-t border-white/[0.06]">
                <div className="flex justify-between items-center mb-6">
                  <div>
                    <div className="text-xs text-white/30 uppercase tracking-wider mb-1">Total Bundle Value</div>
                    <div className="text-2xl md:text-3xl font-light" style={serif}>{c.bundleTotal}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-white/30 uppercase tracking-wider mb-1">Your Budget</div>
                    <div className="text-lg text-emerald-400 font-medium">{c.bundleBudget}</div>
                  </div>
                </div>
                <button onClick={() => navigate('/signup')}
                  className="w-full bg-[#C6A15B] text-black py-3.5 uppercase tracking-[0.15em] text-[13px] font-semibold hover:bg-[#DCC286] transition-all duration-300 flex items-center justify-center gap-2 rounded-lg"
                  data-testid="bundle-cta-btn">
                  Build Your Own Bundle <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </Reveal>
        </div>
      </section>


      {/* ━━━ 3.5 INVESTMENT OPPORTUNITIES ━━━ */}
      <section
        id="investment-opportunities"
        className="relative py-24 md:py-32 overflow-hidden bg-[#080808]"
        data-testid="investment-opportunities-section"
      >
        {/* Subtle gold radial backdrop — luxe, not loud */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-[#C6A15B]/[0.04] rounded-full blur-3xl" />
          <div
            className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage:
                'linear-gradient(rgba(198,161,91,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(198,161,91,0.4) 1px, transparent 1px)',
              backgroundSize: '64px 64px',
            }}
          />
        </div>

        <div className="relative max-w-[1100px] mx-auto px-6 md:px-12">
          <Reveal>
            <div className="text-center mb-14 md:mb-20">
              <span className="text-[#C6A15B] text-[11px] tracking-[0.3em] uppercase mb-4 block" data-testid="invest-section-eyebrow">
                Investment Opportunities
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-light leading-tight mb-5" style={serif}>
                Where capital meets <span className="text-[#C6A15B]">craftsmanship</span>.
              </h2>
              <p className="text-white/40 text-base max-w-[620px] mx-auto leading-relaxed">
                Beyond a lifestyle, ASTIIV BASGAT unlocks a curated investment universe — branded residences, exotic vehicles, and alternative-asset portfolios — each scored, ranked, and matched by our AI to your wealth goals.
              </p>
            </div>
          </Reveal>

          {/* Three pillars */}
          <div className="grid md:grid-cols-3 gap-5 md:gap-6 mb-16">
            {[
              {
                Icon: Building2,
                eyebrow: 'Real Estate',
                title: 'Luxury Real Estate',
                copy: 'Branded residences, sea-front penthouses, signature villas — pre-vetted in Dubai, Mumbai, Goa, and the world\'s most resilient markets.',
                stat: '8–14% IRR',
                statLabel: 'avg. luxury yield',
                tid: 'invest-card-real-estate',
              },
              {
                Icon: Car,
                eyebrow: 'Mobility',
                title: 'Exotic Vehicles',
                copy: 'Limited-edition hypercars, vintage Ferraris, and collector-grade classics tracked as appreciating alternative assets.',
                stat: 'Up to 22%',
                statLabel: 'YoY appreciation',
                tid: 'invest-card-exotic-vehicles',
              },
              {
                Icon: TrendingUp,
                eyebrow: 'Curated Baskets',
                title: 'Investment Portfolios',
                copy: 'Hand-picked, AI-balanced baskets across art, watches, yachts, and private deals — diversified for UHNW resilience.',
                stat: '12 themes',
                statLabel: 'risk-tiered baskets',
                tid: 'invest-card-portfolios',
              },
            ].map(({ Icon, eyebrow, title, copy, stat, statLabel, tid }, idx) => (
              <Reveal key={tid} delay={idx * 0.08}>
                <div
                  className="group relative h-full bg-[#0E0E0E] border border-[#1F1F1F] rounded-2xl p-7 md:p-8 transition-all duration-700 hover:border-[#C6A15B]/40 hover:bg-[#111111]"
                  data-testid={tid}
                >
                  {/* Icon block */}
                  <div className="w-12 h-12 rounded-xl bg-[#C6A15B]/10 border border-[#C6A15B]/20 flex items-center justify-center mb-6 transition-colors group-hover:bg-[#C6A15B]/15">
                    <Icon className="w-5 h-5 text-[#C6A15B]" strokeWidth={1.4} />
                  </div>
                  <span className="text-[10px] tracking-[0.25em] uppercase text-white/30 mb-2 block">{eyebrow}</span>
                  <h3 className="text-xl md:text-2xl font-light mb-3" style={serif}>{title}</h3>
                  <p className="text-white/40 text-sm leading-relaxed mb-6 min-h-[72px]">{copy}</p>
                  {/* Stat strip */}
                  <div className="pt-5 border-t border-white/[0.06] flex items-baseline justify-between">
                    <span className="text-[#C6A15B] text-2xl font-light tracking-tight" style={serif}>{stat}</span>
                    <span className="text-[10px] uppercase tracking-[0.18em] text-white/30">{statLabel}</span>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          {/* AI insights + Premium gate */}
          <Reveal delay={0.2}>
            <div className="relative bg-gradient-to-br from-[#0F0F0F] via-[#0A0A0A] to-[#0F0F0F] border border-[#C6A15B]/15 rounded-2xl p-8 md:p-12 overflow-hidden" data-testid="invest-ai-block">
              <div className="absolute -top-20 -right-20 w-72 h-72 bg-[#C6A15B]/[0.06] rounded-full blur-3xl" />
              <div className="relative grid lg:grid-cols-[auto_1fr_auto] gap-8 lg:gap-12 items-center">
                {/* Left — pulse */}
                <div className="relative shrink-0">
                  <div className="w-20 h-20 rounded-2xl bg-[#C6A15B]/10 border border-[#C6A15B]/30 flex items-center justify-center">
                    <BarChart3 className="w-9 h-9 text-[#C6A15B]" strokeWidth={1.2} />
                  </div>
                  <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-[#C6A15B] animate-pulse" />
                </div>

                {/* Middle — copy */}
                <div className="text-center lg:text-left">
                  <span className="text-[#C6A15B] text-[10px] tracking-[0.3em] uppercase mb-3 block">AI-Driven Insights · Premium Access</span>
                  <h3 className="text-2xl md:text-3xl font-light leading-snug mb-3" style={serif}>
                    Tailored investment strategies, scored in real time.
                  </h3>
                  <p className="text-white/45 text-sm md:text-base leading-relaxed max-w-xl mx-auto lg:mx-0">
                    Our AI engine analyses 40+ market signals — yield, volatility, scarcity, brand pedigree — and matches each opportunity to your risk profile. Available exclusively on Premium and Elite tiers.
                  </p>
                  {/* Tags */}
                  <div className="flex flex-wrap gap-2 mt-5 justify-center lg:justify-start">
                    {['Yield Forecasting', 'Risk Scoring', 'Scarcity Index', 'Portfolio Rebalancing'].map((t) => (
                      <span key={t} className="text-[10px] tracking-[0.15em] uppercase text-white/50 px-3 py-1.5 border border-white/10 rounded-full bg-white/[0.02]">
                        {t}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Right — CTA */}
                <div className="flex flex-col gap-3 shrink-0 w-full lg:w-auto">
                  <button
                    onClick={() => navigate('/subscription')}
                    className="bg-[#C6A15B] text-black px-8 py-3.5 uppercase tracking-[0.15em] text-[12px] font-semibold transition-all hover:bg-[#DCC286] inline-flex items-center justify-center gap-2 whitespace-nowrap"
                    data-testid="invest-premium-cta-btn"
                  >
                    <Crown className="w-4 h-4" /> Unlock Premium
                  </button>
                  <button
                    onClick={() => scrollTo('how-it-works')}
                    className="border border-white/15 text-white/70 hover:text-white hover:bg-white/5 px-8 py-3.5 uppercase tracking-[0.15em] text-[12px] transition-colors inline-flex items-center justify-center"
                    data-testid="invest-learn-more-btn"
                  >
                    Learn How It Works
                  </button>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>


      {/* ━━━ 4. PRICING ━━━ */}
      <section id="pricing" className="py-24 md:py-32 bg-[#0E0E0E]" data-testid="pricing-section">
        <div className="max-w-[1100px] mx-auto px-6 md:px-12">
          <Reveal>
            <div className="text-center mb-16">
              <span className="text-[#C6A15B] text-[11px] tracking-[0.3em] uppercase mb-4 block">Simple Pricing</span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-light mb-4" style={serif}>{c.pricingTitle}</h2>
              <p className="text-white/40 text-base max-w-lg mx-auto">{c.pricingDesc}</p>
            </div>
          </Reveal>

          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {mergedPlans.map((plan, i) => (
              <Reveal key={plan.tier} delay={i * 0.1}>
                <div className={`relative rounded-2xl border p-6 md:p-8 flex flex-col transition-all duration-500 hover:scale-[1.02] ${
                  plan.popular
                    ? 'border-[#C6A15B]/50 bg-gradient-to-b from-[#C6A15B]/8 to-[#0E0E0E] shadow-[0_0_40px_rgba(198,161,91,0.08)]'
                    : 'border-[#1F1F1F] bg-[#0A0A0A] hover:border-[#C6A15B]/20'
                }`} data-testid={`plan-${plan.tier}`}>
                  {plan.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#C6A15B] text-black text-[10px] tracking-[0.15em] uppercase font-bold px-4 py-1 rounded-full">
                      Most Popular
                    </div>
                  )}
                  <div className="flex items-center gap-3 mb-5">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${plan.popular ? 'bg-[#C6A15B]/20' : 'bg-white/5'}`}>
                      <plan.icon className={`w-5 h-5 ${plan.popular ? 'text-[#C6A15B]' : 'text-white/40'}`} strokeWidth={1.5} />
                    </div>
                    <h3 className="text-xl font-medium" style={serif}>{plan.name}</h3>
                  </div>
                  <div className="mb-6">
                    <span className="text-3xl font-semibold">{plan.price}</span>
                    <span className="text-white/30 text-sm ml-1">{plan.period}</span>
                  </div>
                  <ul className="space-y-3 mb-8 flex-1">
                    {plan.features.map(({ text, included }) => (
                      <li key={text} className="flex items-start gap-3 text-sm">
                        {included ? (
                          <Check className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                        ) : (
                          <Lock className="w-4 h-4 text-white/15 mt-0.5 flex-shrink-0" />
                        )}
                        <span className={included ? 'text-white/60' : 'text-white/20'}>{text}</span>
                      </li>
                    ))}
                  </ul>
                  <button onClick={() => navigate('/signup')}
                    className={`w-full py-3 uppercase tracking-[0.12em] text-[12px] font-semibold transition-all duration-300 rounded-lg ${
                      plan.popular
                        ? 'bg-[#C6A15B] text-black hover:bg-[#DCC286]'
                        : 'border border-white/15 text-white hover:border-[#C6A15B]/50 hover:text-[#C6A15B]'
                    }`}
                    data-testid={`plan-${plan.tier}-cta`}>
                    {plan.tier === 'free' ? 'Get Started Free' : `Choose ${plan.name}`}
                  </button>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>


      {/* ━━━ 5. TRUST — ABOUT + TESTIMONIALS ━━━ */}
      <section id="about" className="py-24 md:py-32 bg-[#0A0A0A]" data-testid="trust-section">
        <div className="max-w-[1100px] mx-auto px-6 md:px-12">

          {/* About ASTIIV BASGAT */}
          <Reveal>
            <div className="max-w-3xl mx-auto text-center mb-20">
              <span className="text-[#C6A15B] text-[11px] tracking-[0.3em] uppercase mb-4 block">About ASTIIV BASGAT</span>
              <h2 className="text-3xl md:text-4xl font-light mb-6" style={serif}>Built for people who want more than just a property search</h2>
              <p className="text-white/45 text-base leading-relaxed mb-6">
                {c.aboutDesc}
              </p>
              <div className="flex items-center justify-center gap-6 flex-wrap">
                <div className="flex items-center gap-2 text-sm text-white/35">
                  <Cpu className="w-4 h-4 text-[#C6A15B]/60" />
                  <span>Powered by AI matching</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-white/35">
                  <Shield className="w-4 h-4 text-[#C6A15B]/60" />
                  <span>Bank-grade security</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-white/35">
                  <Building2 className="w-4 h-4 text-[#C6A15B]/60" />
                  <span>{c.aboutBadge}</span>
                </div>
              </div>
            </div>
          </Reveal>

          {/* Founder */}
          <Reveal delay={0.1}>
            <div className="max-w-2xl mx-auto text-center mb-20 py-10 px-8 rounded-2xl border border-white/[0.04] bg-[#0E0E0E]" data-testid="founder-section">
              <Quote className="w-8 h-8 text-[#C6A15B]/30 mx-auto mb-4" />
              <p className="text-white/50 text-base md:text-lg leading-relaxed italic mb-6" style={serif}>
                &ldquo;We built ASTIIV BASGAT because the luxury market deserved intelligence, not keyword searches. Every person deserves to see what their budget can truly unlock.&rdquo;
              </p>
              <div>
                <div className="text-white font-medium text-sm">Harshit Bhagat</div>
                <div className="text-white/30 text-xs tracking-wider uppercase">Founder, ASTIIV BASGAT</div>
              </div>
            </div>
          </Reveal>

          {/* Testimonials */}
          <Reveal delay={0.15}>
            <div className="text-center mb-12">
              <span className="text-[#C6A15B] text-[11px] tracking-[0.3em] uppercase mb-4 block">What Our Users Say</span>
            </div>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {c.testimonials.map(({ name, role, text }, i) => (
              <Reveal key={name} delay={0.15 + i * 0.08}>
                <div className="bg-[#0E0E0E] border border-[#1F1F1F] rounded-2xl p-6 md:p-8 hover:border-[#C6A15B]/15 transition-all duration-500" data-testid={`testimonial-${i}`}>
                  <p className="text-white/40 text-sm leading-relaxed mb-6">&ldquo;{text}&rdquo;</p>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-[#C6A15B]/15 flex items-center justify-center">
                      <span className="text-[#C6A15B] text-sm font-semibold">{name.charAt(0)}</span>
                    </div>
                    <div>
                      <div className="text-white text-sm font-medium">{name}</div>
                      <div className="text-white/25 text-xs">{role}</div>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>


      {/* ━━━ 6. FINAL CTA ━━━ */}
      <section className="py-24 md:py-32 text-center relative overflow-hidden" data-testid="final-cta-section"
        style={{ background: 'linear-gradient(180deg, #0A0A0A 0%, #110F0A 50%, #0A0A0A 100%)' }}>
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at 50% 30%, rgba(198,161,91,0.04) 0%, transparent 70%)' }} />
        <div className="max-w-[800px] mx-auto px-6 relative z-10">
          <Reveal>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-light mb-5" style={serif}>
              {c.finalCta}
            </h2>
            <p className="text-white/40 text-base md:text-lg mb-10 max-w-lg mx-auto leading-relaxed">
              {c.finalDesc}
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <button onClick={() => navigate('/signup')}
              className="bg-[#C6A15B] text-black px-8 sm:px-14 py-4 uppercase tracking-[0.2em] text-[12px] sm:text-[13px] font-semibold transition-all duration-500 hover:bg-[#DCC286] hover:shadow-[0_0_50px_rgba(198,161,91,0.4)] inline-flex items-center gap-3 max-w-full whitespace-nowrap"
              data-testid="final-cta-btn">
              {c.finalCtaBtn} <ChevronRight className="w-5 h-5 flex-shrink-0" />
            </button>
          </Reveal>
        </div>
      </section>

      {/* ━━━ FOR PARTNERS · BOTTOM CTA ━━━ */}
      <section className="py-20 px-6 md:px-12 border-t border-white/[0.04] bg-[#080808]" data-testid="for-partners-section">
        <div className="max-w-[1100px] mx-auto">
          <Reveal>
            <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
              <div>
                <p className="text-[11px] text-[#C6A15B] tracking-[0.3em] uppercase mb-3">For Partners</p>
                <h3 className="text-2xl sm:text-3xl font-light leading-tight" style={serif}>
                  Develop. Broker. Curate.<br className="hidden sm:block" />
                  <span className="text-white/45">Reach our verified buyers.</span>
                </h3>
                <p className="text-white/35 text-sm mt-4 max-w-md">
                  Real-estate developers, luxury car dealers, yacht brokers, and concierge brands — list with ASTIIV BASGAT and get matched with HNI &amp; NRI buyers actively seeking your inventory.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto lg:shrink-0">
                <button
                  onClick={() => navigate('/partner')}
                  className="bg-[#C6A15B] text-black px-6 sm:px-8 py-3.5 uppercase tracking-[0.15em] text-[11px] sm:text-[12px] font-semibold transition-colors hover:bg-[#DCC286] inline-flex items-center justify-center gap-2 w-full sm:w-auto whitespace-nowrap"
                  data-testid="for-partners-cta-btn"
                >
                  Become a Partner <ChevronRight className="w-4 h-4" />
                </button>
                <button
                  onClick={() => navigate('/partner/login')}
                  className="border border-white/15 text-white/70 hover:text-white hover:bg-white/5 px-6 sm:px-8 py-3.5 uppercase tracking-[0.15em] text-[11px] sm:text-[12px] transition-colors inline-flex items-center justify-center w-full sm:w-auto whitespace-nowrap"
                  data-testid="for-partners-login-btn"
                >
                  Partner Login
                </button>
              </div>
            </div>
          </Reveal>
        </div>
      </section>


      {/* ━━━ FOOTER ━━━ */}
      <footer className="bg-[#0A0A0A] border-t border-white/[0.04] pt-16 pb-10" data-testid="landing-footer">
        <div className="max-w-[1100px] mx-auto px-6 md:px-12 flex flex-col items-center text-center">
          <div className="mb-3">
            <Logo to={null} size="lg" wordmarkClassName="text-white" tracking="tracking-[0.1em]" testId="landing-footer-logo" />
          </div>
          <p className="text-white/25 text-sm mb-10">AI-powered lifestyle intelligence</p>
          <div className="flex flex-wrap justify-center gap-6 text-[11px] text-white/30 uppercase tracking-[0.15em] mb-10">
            <Link to="/login" className="hover:text-white transition-colors" data-testid="footer-signin-link">Sign In</Link>
            <Link to="/signup" className="hover:text-white transition-colors" data-testid="footer-signup-link">Create Account</Link>
            <Link to="/partner" className="hover:text-white transition-colors" data-testid="footer-partner-link">Partners</Link>
            <Link to="/legal/terms" className="hover:text-white transition-colors" data-testid="footer-terms-link">Terms</Link>
            <Link to="/legal/privacy" className="hover:text-white transition-colors" data-testid="footer-privacy-link">Privacy</Link>
          </div>
          <p className="text-[10px] text-white/10 tracking-[0.2em]">{c.footerLine}</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
